import type { Concept } from '@yuan-math/curriculum';

/**
 * The rest of the map.
 *
 * These concepts have no lessons yet, and are marked `planned`. They are in the
 * graph from day one anyway, for two reasons: a ten-year-old should be able to
 * see that the fractions they are doing today are on the road to calculus, and
 * the architecture has to prove — before the content exists — that it can carry
 * university mathematics without a rewrite (§16, §31).
 */
const planned = (
  id: string,
  title: string,
  summary: string,
  strand: Concept['strand'],
  levels: Concept['levels'],
  prerequisites: string[],
): Concept => ({ id, title, summary, strand, levels, prerequisites, status: 'planned' });

export const PLANNED_CONCEPTS: Concept[] = [
  /*
   * Middle school is built: Grades 6, 7 and 8 live in `middle.ts`,
   * `middle-upper.ts` and `grade-eight.ts`. What remains below begins at
   * Algebra I.
   */
  /*
   * High school is built: Algebra I, Geometry and Algebra II in
   * `algebra-one.ts`, `geometry-hs.ts` and `algebra-two.ts`, Grade 12
   * precalculus in `precalculus.ts`, and the Grade 12 calculus pathway —
   * limits through the fundamental theorem — in `calculus.ts`. What remains
   * below is what those courses lead to.
   */
  planned('statistical-inference', 'Statistical Inference', 'What a sample can and cannot tell you about a population.', 'statistics', ['grade-12', 'university'], ['probability-distributions']),

  /* ----------------------------------------------------------- university */
  /*
   * First-year calculus is built: the twenty concepts of Calculus II —
   * techniques of integration, the physical applications, sequences and series,
   * power series, and parametric and polar curves — live in `calculus-two.ts`.
   * Three placeholders used to stand here for that whole year, and they were
   * deleted rather than kept beside it: a map offering "Techniques of
   * Integration (planned)" next to five techniques a student can study today
   * describes the platform of a year ago.
   */
  planned('multivariable', 'Multivariable Functions', 'Functions of several variables, and partial derivatives.', 'calculus', ['university'], ['taylor-series', 'polar-calculus']),
  planned('multiple-integrals', 'Multiple Integrals', 'Integrating over regions of the plane and of space.', 'calculus', ['university'], ['multivariable']),
  planned('vector-calculus', 'Vector Calculus', 'Line and surface integrals, and the great theorems that connect them.', 'calculus', ['university'], ['multiple-integrals']),
  /*
   * Linear algebra used to be four planned nodes here — matrices, vector
   * spaces, linear transformations and eigenvalues. It is built now, in
   * `linear-algebra.ts`, and the four ids survive with the same meanings so
   * that `ode` and `abstract-algebra-intro` still hang off the right places.
   */
  planned('logic-proof', 'Logic and Proof', 'What it means to know something in mathematics.', 'discrete', ['university'], ['conditional-statements']),
  planned('set-theory', 'Sets and Relations', 'The language the rest of mathematics is written in.', 'discrete', ['university'], ['logic-proof']),
  planned('graph-theory', 'Graph Theory', 'Networks of nodes and edges, and what can be proved about them.', 'discrete', ['university'], ['set-theory', 'combinatorics-intro']),
  planned('ode', 'Differential Equations', 'Equations relating a function to its own rate of change.', 'differential-equations', ['university'], ['partial-fractions', 'eigenvalues']),
  planned('real-analysis', 'Real Analysis', 'Calculus rebuilt on rigorous foundations.', 'analysis', ['advanced'], ['taylor-series', 'set-theory']),
  planned('complex-analysis', 'Complex Analysis', 'Calculus over the complex numbers, where everything is nicer.', 'analysis', ['advanced'], ['real-analysis']),
  planned('abstract-algebra-intro', 'Abstract Algebra', 'Groups, rings and fields: structure studied for its own sake.', 'abstract-algebra', ['advanced'], ['set-theory', 'eigenvalues']),
  planned('number-theory-advanced', 'Number Theory', 'Congruences, primes and the deep behaviour of the integers.', 'number-theory', ['advanced'], ['prime-factorization', 'abstract-algebra-intro']),
  planned('topology-intro', 'Topology', 'Continuity and space, without distance.', 'topology', ['advanced'], ['real-analysis', 'set-theory']),

  /* ---------------------------------------------------------- competition */
  planned('competition-algebra', 'Competition Algebra', 'Clever manipulation: symmetry, substitution and factoring tricks.', 'competition', ['grade-9', 'grade-10'], ['quadratics']),
  planned('competition-number-theory', 'Competition Number Theory', 'Modular arithmetic and divisibility arguments.', 'competition', ['grade-10'], ['prime-factorization']),
  planned('competition-combinatorics', 'Competition Combinatorics', 'Counting arguments, bijections and the pigeonhole principle.', 'competition', ['grade-10', 'grade-11'], ['combinatorics-intro']),
  planned('competition-geometry', 'Competition Geometry', 'Synthetic geometry, similar triangles and cyclic quadrilaterals.', 'competition', ['grade-10', 'grade-11'], ['pythagoras', 'trig-ratios']),
  planned('olympiad-inequalities', 'Inequalities', 'AM–GM, Cauchy–Schwarz and the art of bounding.', 'competition', ['grade-11', 'grade-12'], ['competition-algebra']),
  planned('olympiad-proof', 'Olympiad Proof Techniques', 'Induction, contradiction, invariants and extremal arguments.', 'competition', ['grade-11', 'grade-12'], ['competition-combinatorics', 'logic-proof']),
];
